import { forwardRef, type ButtonHTMLAttributes } from "react";

import "../styles/payment-buttons.css";

/**
 * Stub/local official payment faces. Live Revolut mode mounts the SDK
 * widgets in the same 48×100% slots. No homemade Apple/Google/card paths.
 */

const slotClass = "block h-12 w-full";

export function WalletFallbackStack() {
  return (
    <div className="flex w-full flex-col gap-3" data-testid="order-wallets">
      <ApplePayFallback />
      <GooglePayFallback />
      <RevolutPayFallback />
      <PayMethodDivider />
    </div>
  );
}

/** Official Revolut R (vendored) + “Revolut Pay”. Does not clip the mark. */
function RevolutPayFallback() {
  return (
    <button
      type="button"
      disabled
      data-testid="order-revolut-pay"
      aria-label="Revolut Pay (unavailable in this environment)"
      className="flex h-12 w-full items-center justify-center gap-2 rounded bg-black px-6 text-white"
      style={{ overflow: "visible" }}
    >
      <img
        src="/payment-marks/revolut-r.svg"
        alt=""
        width={22}
        height={22}
        className="h-[22px] w-auto invert"
        style={{ objectFit: "contain" }}
      />
      <span
        className="text-white"
        style={{ fontFamily: "Inter, system-ui, sans-serif", fontSize: 16, fontWeight: 600 }}
      >
        Revolut Pay
      </span>
    </button>
  );
}

/** Apple’s own `<apple-pay-button>` — do not wrap in overflow:hidden.
 *  The SDK may set [hidden] when Wallet is missing; CSS unhides the
 *  official shadow-DOM face. Do not put our SVG inside this element. */
function ApplePayFallback() {
  return (
    <div
      className={slotClass}
      data-testid="order-apple-pay"
      aria-label="Apple Pay (unavailable in this environment)"
      style={{ pointerEvents: "none", overflow: "visible" }}
    >
      <apple-pay-button buttonstyle="black" type="plain" locale="en-IE" />
    </div>
  );
}

/** Official Google Pay dark button CSS + vendored dark_gpay.svg. */
function GooglePayFallback() {
  return (
    <button
      type="button"
      disabled
      className="gpay-button"
      data-testid="order-google-pay"
      aria-label="Google Pay (unavailable in this environment)"
    />
  );
}

export function PayMethodDivider() {
  return (
    <div
      className="flex items-center gap-3"
      data-testid="order-pay-divider"
      role="separator"
    >
      <span className="h-px flex-1 bg-border" />
      <span className="text-xs text-muted-fg">or pay with card</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

type CardButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  label: string;
};

/** Light official-style card button. Scheme marks are vendored SVGs, not paths. */
export const PayByCardButton = forwardRef<HTMLButtonElement, CardButtonProps>(
  ({ label, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      data-testid="order-pay"
      className={`flex h-12 w-full items-center justify-between rounded border border-[#d4d4d4] bg-white px-4 text-[15px] font-medium text-neutral-900 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:cursor-not-allowed${
        className ? ` ${className}` : ""
      }`}
      {...props}
    >
      <span className="min-w-0 truncate">{label}</span>
      <span className="flex shrink-0 items-center gap-2" aria-hidden="true">
        <img
          src="/payment-marks/visa.svg"
          alt=""
          className="h-6 w-auto"
          height={24}
        />
        <img
          src="/payment-marks/mastercard.svg"
          alt=""
          className="h-6 w-auto"
          height={24}
        />
        <img
          src="/payment-marks/amex.svg"
          alt=""
          className="h-6 w-auto"
          height={24}
        />
      </span>
    </button>
  ),
);
PayByCardButton.displayName = "PayByCardButton";
